import React, { useState } from 'react';
import { validateCharacter, validateSetting } from '../utils/validators';
import './CharacterSettingForm.css';

/**
 * Form component for character and setting inputs
 * 
 * @param {string} character - Current character value
 * @param {string} setting - Current setting value
 * @param {function} onChange - Function called when inputs change with { character, setting }
 * @param {boolean} isDisabled - Whether the form should be disabled
 * @returns {JSX.Element}
 */
const CharacterSettingForm = ({ 
  character = '', 
  setting = '', 
  onChange,
  isDisabled = false
}) => {
  const [errors, setErrors] = useState({
    character: '',
    setting: ''
  });

  // Validate character input on blur
  const validateCharacterField = () => {
    const result = validateCharacter(character);
    setErrors(prev => ({ ...prev, character: result.message }));
    return result.isValid;
  };

  // Validate setting input on blur
  const validateSettingField = () => {
    const result = validateSetting(setting);
    setErrors(prev => ({ ...prev, setting: result.message }));
    return result.isValid;
  };

  const handleCharacterChange = (e) => {
    const value = e.target.value;
    
    // Clear error when typing
    if (errors.character) {
      setErrors(prev => ({ ...prev, character: '' }));
    }
    
    // Notify parent component
    if (onChange) {
      onChange({ character: value, setting });
    }
  };

  const handleSettingChange = (e) => {
    const value = e.target.value;
    
    // Clear error when typing
    if (errors.setting) {
      setErrors(prev => ({ ...prev, setting: '' }));
    }
    
    // Notify parent component
    if (onChange) {
      onChange({ character, setting: value });
    }
  };

  return (
    <div className="character-setting-form">
      <div className="form-group">
        <label className="form-label" htmlFor="character">
          Main Character
        </label>
        <input
          type="text"
          id="character"
          className={`text-input ${errors.character ? 'error' : ''}`}
          value={character}
          onChange={handleCharacterChange}
          onBlur={validateCharacterField}
          placeholder="A brave little fox named Luna..."
          disabled={isDisabled}
          maxLength={100}
          aria-invalid={!!errors.character}
          aria-describedby={errors.character ? "character-error" : undefined}
        />
        {errors.character && (
          <div className="error-message" id="character-error">{errors.character}</div>
        )}
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="setting">
          Setting
        </label>
        <input
          type="text"
          id="setting"
          className={`text-input ${errors.setting ? 'error' : ''}`}
          value={setting}
          onChange={handleSettingChange}
          onBlur={validateSettingField}
          placeholder="In an enchanted forest..."
          disabled={isDisabled}
          maxLength={100}
          aria-invalid={!!errors.setting}
          aria-describedby={errors.setting ? "setting-error" : undefined}
        />
        {errors.setting && (
          <div className="error-message" id="setting-error">{errors.setting}</div>
        )}
      </div>
    </div>
  );
};

export default CharacterSettingForm;
